import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Radio, Trash2, Plus, Link as LinkIcon } from 'lucide-react';
import { API } from '../config/api';

/**
 * MyCategoriesManager
 *
 * Pro-only surface for personal MyLaps categories. The user pastes a
 * link/event id and gives it a name; the backend resolves it via
 * `POST /speedhive/my-categories` and the AutoLiveManager picks it up
 * on the next tick, capturing sessions automatically. Sessions from a
 * personal category flow into the same `race_sessions` collection and
 * show up in the RaceList tree under that category's name for the
 * owner (only).
 */
const MyCategoriesManager = ({ isPro }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    if (!isPro) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/speedhive/my-categories`, { withCredentials: true });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error('No pude cargar tus categorías');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isPro]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || !url.trim()) {
      toast.error('Completá nombre y link');
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(
        `${API}/speedhive/my-categories`,
        { name: name.trim(), url: url.trim() },
        { withCredentials: true },
      );
      setItems([r.data, ...items]);
      setName('');
      setUrl('');
      toast.success(`✓ Categoría agregada. La captura automática arranca en unos segundos.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pude crear la categoría');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`¿Eliminar "${cat.name}"? Se detiene la captura pero las sesiones ya guardadas se conservan.`)) return;
    try {
      await axios.delete(`${API}/speedhive/my-categories/${cat.id}`, { withCredentials: true });
      setItems(items.filter((i) => i.id !== cat.id));
      toast.success('Categoría eliminada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pude eliminar');
    }
  };

  if (!isPro) {
    return (
      <Card className="bg-neutral-900 border-amber-700/40" data-testid="my-categories-upsell">
        <CardContent className="py-6 text-sm text-amber-300">
          Función Pro — actualizá tu plan para pegar tu propio link de MyLaps y capturar las sesiones del fin de semana automáticamente.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-neutral-900 border-red-900/30" data-testid="my-categories-manager">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Radio className="w-5 h-5 text-red-500" />
          Mis categorías MyLaps
        </CardTitle>
        <CardDescription className="text-gray-400">
          Pegá el link del evento en MyLaps/Speedhive y capturamos automáticamente todas las sesiones del fin de semana.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={create} className="grid gap-2 md:grid-cols-[1fr_2fr_auto]" data-testid="my-categories-form">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre (ej. Mi Turismo Pista)"
            className="h-10 px-3 rounded-md bg-neutral-950 border border-neutral-800 text-sm text-white placeholder-gray-500 focus:border-red-600 focus:outline-none"
            data-testid="my-categories-name"
            maxLength={80}
          />
          <div className="relative">
            <LinkIcon className="w-4 h-4 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://speedhive.mylaps.com/livetiming/... o ID"
              className="w-full h-10 pl-8 pr-3 rounded-md bg-neutral-950 border border-neutral-800 text-sm text-white placeholder-gray-500 focus:border-red-600 focus:outline-none"
              data-testid="my-categories-url"
            />
          </div>
          <Button
            type="submit"
            disabled={creating}
            className="bg-red-600 hover:bg-red-700 text-white h-10"
            data-testid="my-categories-submit"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Agregar</>}
          </Button>
        </form>

        {loading && (
          <div className="text-center py-4 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando…
          </div>
        )}

        {!loading && items.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4" data-testid="my-categories-empty">
            Todavía no agregaste categorías. Pegá arriba el link del evento en MyLaps y arrancamos.
          </p>
        )}

        {items.length > 0 && (
          <ul className="divide-y divide-neutral-800/60" data-testid="my-categories-list">
            {items.map((cat) => (
              <li key={cat.id} className="flex items-center justify-between py-2" data-testid={`my-category-${cat.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-white truncate">{cat.name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {cat.live_event_id ? `live=${cat.live_event_id}` : ''}
                    {cat.org_id ? ` · org=${cat.org_id}` : ''}
                  </div>
                </div>
                <Button
                  onClick={() => remove(cat)}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-500 hover:text-red-400 hover:bg-red-950/30"
                  data-testid={`my-category-delete-${cat.id}`}
                  title="Eliminar categoría"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default MyCategoriesManager;
